/* eslint-disable no-nested-ternary */
import { IconChevronDown, IconCloseSmall } from '@/icons';
import React, { useState, useRef } from 'react';
import { useClickAway } from 'react-use';
import { useIntl } from 'react-intl';
import {
  StyledSelectContainer,
  SelectContent,
  SelectValues,
  SingleSelectValue,
  Placeholder,
  ChevronIcon,
  DropdownMenu,
  SearchInput,
  OptionsList,
  NoOptionsMessage,
  OptionContent,
  OptionSelected,
  StyledErrorMessage,
  Option,
  MultiSelectTag,
  ClearMultiTagButton,
  StyledMultiSelectContainer,
  SelectActions,
} from './styles';
import { Flex } from '../Flex/Flex';
import { T } from '../Text/Text';
import { Button } from '../Button/Button';
import { useSelectState, useKeyControls } from './selectHooks';

// Types
export type SelectOption = {
  value: any;
  label: string | React.ReactNode;
} & Record<string, any>;

export interface SelectProps {
  options?: SelectOption[];
  value?: SelectOption | null;
  onChange: (value: SelectOption | null) => void;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  error?: string | boolean;
  errorPosition?: 'top' | 'bottom';
  required?: boolean;
  isSearchable?: boolean;
  className?: string;
  testId?: string;
  icon?: React.ReactNode;
}

export interface MultiSelectProps {
  options?: SelectOption[];
  value?: SelectOption[] | null;
  onChange: (value: SelectOption[] | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  testId?: string;
  required?: boolean;
  errorPosition?: 'top' | 'bottom';
  size?: 'small' | 'medium';
  error?: string | boolean;
  icon?: React.ReactNode;
  title: string;
}

export const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options = [],
      value = null,
      onChange,
      placeholder = 'Select an option...',
      size = 'medium',
      disabled = false,
      error,
      errorPosition = 'top',
      required = false,
      className = '',
      testId,
      icon = null,
      title,
    },
    ref,
  ) => {
    const [selectedOptions, setSelectedOptions] = useState<
      SelectOption[] | null
    >(value);
    const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
      null,
    );

    const {
      isOpen,
      setIsOpen,
      searchTerm,
      setSearchTerm,
      filteredOptions,
      focusedIndex,
      setFocusedIndex,
    } = useSelectState(options, true, selectedOptions ?? undefined, undefined);

    const containerRef = useRef<HTMLDivElement>(null);

    useClickAway(containerRef, () => {
      requestAnimationFrame(() => {
        setIsOpen(false);
        setSearchTerm('');
      });
    });

    const intl = useIntl();

    const handleOptionClick = (option: SelectOption) => {
      if (!option) return;
      const safeSelectedOptions = selectedOptions ?? [];
      setSelectedOptions([...safeSelectedOptions, option]);
      onChange([...safeSelectedOptions, option]);
      setIsOpen(false);
      setSearchTerm('');
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedOptions([]);
      onChange(null);
    };

    const { handleKeyDown } = useKeyControls(
      focusedIndex,
      filteredOptions,
      setFocusedIndex,
      isOpen,
      setIsOpen,
      handleOptionClick,
      setSelectedOption,
      setSelectedOptions,
      true,
    );

    return (
      <div ref={ref}>
        <div style={{ position: 'relative' }}>
          <Flex align="center" justify="space-between">
            <T body1>{title}</T>
            <div ref={containerRef} style={{ position: 'relative' }}>
              <StyledMultiSelectContainer
                error={error || (required && !value)}
                $size={size}
                $isOpen={isOpen}
                $disabled={disabled}
                className={className}
                data-testid={testId}
                onKeyDown={handleKeyDown}
                tabIndex={0}
              >
                <Flex
                  align="center"
                  justify="center"
                  onClick={() => !disabled && setIsOpen(!isOpen)}
                  padding={0}
                >
                  {icon ?? null}
                </Flex>
                <SearchInput
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsOpen(true);
                  }}
                  placeholder={placeholder}
                  $inputContentLength={placeholder.length}
                  $size={size}
                  onFocus={() => setIsOpen(true)}
                />
                <ChevronIcon
                  $isOpen={isOpen}
                  onClick={() => !disabled && setIsOpen(!isOpen)}
                >
                  <IconChevronDown />
                </ChevronIcon>
              </StyledMultiSelectContainer>
              {isOpen && !disabled && (
                <DropdownMenu>
                  <OptionsList>
                    {filteredOptions.length === 0 ? (
                      <NoOptionsMessage $size={size}>
                        No options found
                      </NoOptionsMessage>
                    ) : (
                      filteredOptions.map((option) => {
                        return (
                          <Option
                            key={option.value}
                            $size={size}
                            $isSelected={selectedOption?.label === option.label}
                            onClick={() => handleOptionClick(option)}
                          >
                            <OptionContent>
                              <span>{option.label}</span>
                            </OptionContent>
                          </Option>
                        );
                      })
                    )}
                  </OptionsList>
                </DropdownMenu>
              )}
              {required && !value && (
                <StyledErrorMessage errorPosition={errorPosition}>
                  {intl.formatMessage({
                    defaultMessage: 'Required',
                    id: 'Seanpx',
                  })}
                </StyledErrorMessage>
              )}
            </div>
          </Flex>

          <Flex direction="column" gap={8}>
            <SelectValues
              style={{
                paddingTop:
                  selectedOptions && selectedOptions.length > 0 ? '8px' : '0',
              }}
            >
              {selectedOptions &&
                selectedOptions.length > 0 &&
                selectedOptions.map((option) => (
                  <MultiSelectTag $size={size}>
                    {typeof option.label === 'string'
                      ? option.label
                      : React.isValidElement(option.label)
                        ? option.label
                        : JSON.stringify(option.label)}
                    <ClearMultiTagButton
                      $size={size}
                      onClick={(e) => {
                        e.stopPropagation();
                        const newSelectedOptions = selectedOptions.filter(
                          (o) => o.value !== option.value,
                        );
                        setSelectedOptions(newSelectedOptions);
                        onChange(newSelectedOptions);
                      }}
                      disabled={disabled}
                      type="button"
                    >
                      <IconCloseSmall />
                    </ClearMultiTagButton>
                  </MultiSelectTag>
                ))}
            </SelectValues>

            {selectedOptions && selectedOptions.length > 0 && (
              <Button
                mode="link"
                onClick={handleClear}
                disabled={disabled}
                size="minimum"
              >
                <T body1 underline>
                  Clear All
                </T>
              </Button>
            )}
          </Flex>

          {error && typeof error !== 'boolean' && (
            <StyledErrorMessage errorPosition={errorPosition}>
              {error}
            </StyledErrorMessage>
          )}
        </div>
      </div>
    );
  },
);

const CustomSelect = React.forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      options = [],
      value = null,
      onChange,
      placeholder = 'Select an option...',
      size = 'medium',
      disabled = false,
      error,
      errorPosition = 'top',
      required = false,
      isSearchable = true,
      className = '',
      testId,
      icon = null,
    },
    ref,
  ) => {
    const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
      value,
    );

    const {
      isOpen,
      setIsOpen,
      searchTerm,
      setSearchTerm,
      isTyping,
      setIsTyping,
      filteredOptions,
      focusedIndex,
      setFocusedIndex,
      currentIndex,
    } = useSelectState(options, false, undefined, selectedOption ?? undefined);

    const containerRef = useRef<HTMLDivElement>(null);

    const intl = useIntl();

    const handleOptionClick = (option: SelectOption) => {
      if (!option) return;
      setSelectedOption(option);
      onChange(option);
      setIsOpen(false);
      setSearchTerm(
        selectedOption?.label && typeof selectedOption.label === 'string'
          ? selectedOption.label
          : '',
      );
      setIsTyping(false);
    };

    const { handleKeyDown } = useKeyControls(
      focusedIndex,
      filteredOptions,
      setFocusedIndex,
      isOpen,
      setIsOpen,
      handleOptionClick,
      setSelectedOption,
      undefined,
    );

    useClickAway(containerRef, () => {
      requestAnimationFrame(() => {
        setIsOpen(false);
        setSearchTerm(
          selectedOption?.label && typeof selectedOption.label === 'string'
            ? selectedOption.label
            : '',
        );
        setIsTyping(false);
        setFocusedIndex(currentIndex ?? 0);
      });
    });

    return (
      <div ref={ref}>
        <div ref={containerRef} style={{ position: 'relative' }}>
          <StyledSelectContainer
            error={error || (required && !value)}
            $size={size}
            $isOpen={isOpen}
            $disabled={disabled}
            className={className}
            data-testid={testId}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            onClick={() => {
              if (!disabled) setIsOpen(!isOpen);
              setSearchTerm('');
            }}
            onBlur={() => {
              setIsTyping(false);
              if (selectedOption?.value) onChange(selectedOption);
            }}
          >
            <Flex align="center" justify="center">
              {icon ?? null}
            </Flex>
            <SelectContent size={size}>
              {isSearchable ? (
                <SearchInput
                  type="text"
                  value={
                    isTyping
                      ? (searchTerm ?? '')
                      : selectedOption?.label &&
                          typeof selectedOption.label === 'string'
                        ? selectedOption.label
                        : ''
                  }
                  onKeyDown={(e) => {
                    if (!isTyping) {
                      if (
                        e.key.length === 1 ||
                        e.key === 'Backspace' ||
                        e.key === 'Delete'
                      ) {
                        setSearchTerm('');
                        setIsTyping(true);
                        setIsOpen(true);
                      }
                    }
                  }}
                  onChange={(e) => {
                    const newValue = e.target.value;

                    if (isTyping) {
                      setSearchTerm(newValue);
                    }

                    setIsOpen(true);
                  }}
                  onBlur={() => {
                    setIsTyping(false);

                    if (selectedOption?.value) onChange(selectedOption);
                  }}
                  placeholder={placeholder ?? 'Search options...'}
                  $size={size}
                  $inputContentLength={
                    selectedOption?.label &&
                    typeof selectedOption.label === 'string'
                      ? selectedOption.label.length
                      : placeholder.length
                  }
                  onClick={() => !disabled && setIsOpen(true)}
                />
              ) : (
                <SelectValues>
                  {selectedOption ? (
                    <SingleSelectValue>
                      {typeof selectedOption.label === 'string'
                        ? selectedOption.label
                        : React.isValidElement(selectedOption.label)
                          ? selectedOption.label
                          : JSON.stringify(selectedOption.label)}
                    </SingleSelectValue>
                  ) : (
                    <Placeholder $size={size}>{placeholder}</Placeholder>
                  )}
                </SelectValues>
              )}
              <SelectActions onClick={() => !disabled && setIsOpen(!isOpen)}>
                <ChevronIcon $isOpen={isOpen}>
                  <IconChevronDown />
                </ChevronIcon>
              </SelectActions>
            </SelectContent>
          </StyledSelectContainer>

          {isOpen && !disabled && (
            <DropdownMenu>
              <OptionsList>
                {filteredOptions.length === 0 ? (
                  <NoOptionsMessage $size={size}>
                    No options found
                  </NoOptionsMessage>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = selectedOption?.value === option.value;
                    return (
                      <Option
                        key={option.value}
                        $size={size}
                        $isSelected={isSelected}
                        onClick={() => handleOptionClick(option)}
                      >
                        <OptionContent>
                          <span>{option.label}</span>
                          {isSelected && <OptionSelected />}
                        </OptionContent>
                      </Option>
                    );
                  })
                )}
              </OptionsList>
            </DropdownMenu>
          )}

          {error && typeof error !== 'boolean' && (
            <StyledErrorMessage errorPosition={errorPosition}>
              {error}
            </StyledErrorMessage>
          )}
          {required && !value && (
            <StyledErrorMessage errorPosition={errorPosition}>
              {intl.formatMessage({
                defaultMessage: 'Required',
                id: 'Seanpx',
              })}
            </StyledErrorMessage>
          )}
        </div>
      </div>
    );
  },
);

export default CustomSelect;
